import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  Checkbox,
  FormControlLabel,
  FormLabel,
  FormGroup,
  FormHelperText,
  Tooltip,
  Autocomplete,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BadgeIcon from '@mui/icons-material/Badge';

import { useSnackbar } from "notistack";
import StyledTextField from "../StyledComponents/StyledTextField";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import { useStyles } from "./styles";
import httpRequest from "../../httpRequest";
import { Card } from '@ui5/webcomponents-react';
import { Label, Input, CheckBox, Switch, ActionSheet } from '@ui5/webcomponents-react';

function UsersTable({ users, onUserUpdated }) {
  const classes = useStyles();
  const [selectedUser, setSelectedUser] = useState({ name: "", email: "", password: "", employee_code: "", dms_user_id: "" });
  const [showModal, setShowModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [search, setSearch] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState({});
  const [employees, setEmployees] = useState([]); // For Employee Code dropdown
  const [dmsUsers, setDmsUsers] = useState([]); // For Select DMS User dropdown

  const [editingPermissions, setEditingPermissions] = useState(false);
  const [customPermissions, setCustomPermissions] = useState({
    can_add_invoice: false,
    can_access_scans: false,
    can_access_all_scans: false,
    can_access_all_company_scans: false,
    can_delete_invoice: false,
    can_add_expense: false,
    can_access_expense: false,
    can_general_scan: false,
    can_user_management: false,
    can_settings: false,
    can_vendor: false,
    can_configurator: false,
    can_delete_documents: false,
    can_add_documents: false,
    can_manage_documents: false,
    can_function_dashboard: false,
    can_company_dashboard: false,
    can_enterprise_dashboard: false,
  });

  const [emailError, setEmailError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const resetPasswordState = () => {
    setNewPassword("");
    setConfirmPassword("");
  };
  const [dialogTitle, setDialogTitle] = useState('');
  const [formData, setFormData] = useState({
    name: "",
    can_add_invoice: false,
    can_access_scans: false,
    can_access_all_scans: false,
    can_access_all_company_scans: false,
    can_delete_invoice: false,
    can_add_expense: false,
    can_access_expense: false,
    can_general_scan: false,
    can_user_management: false,
    can_settings: false,
    can_vendor: false,
    can_configurator: false,
    can_delete_documents: false,
    can_add_documents: false,
    can_manage_documents: false,
    can_function_dashboard: false,
    can_company_dashboard: false,
    can_enterprise_dashboard: false,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);

  // Fetch DMS users
  useEffect(() => {
    const fetchDmsUsers = async () => {
      try {
        const dmsSettingsResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/dms-settings`);
        const { dms_url, api_key } = dmsSettingsResponse.data;

        if (!dms_url || !api_key) {
          enqueueSnackbar("DMS integration not configured", { variant: "warning" });
          return;
        }

        const response = await httpRequest.get(`${dms_url}/users/dropdown`, {
          headers: { "X-API-KEY": api_key },
        });
        console.log("DMS Users Response:", response.data);
        setDmsUsers(response.data);
      } catch (error) {
        console.error("Error fetching DMS users:", error);
        enqueueSnackbar("Error fetching DMS users", { variant: "error" });
      }
    };
    fetchDmsUsers();
  }, []);

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-roles`);
      const filteredRoles = response.data.roles.filter(role => role.id !== 1);
      setRoles(filteredRoles);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-employees`);
        console.log("Employees Response:", response.data.employees);
        setEmployees(response.data.employees);
      } catch (error) {
        console.error("Error fetching employees:", error);
        enqueueSnackbar("Error fetching employees", { variant: "error" });
      }
    };
    fetchEmployees();
  }, []);

  const handleEdit = (event, user) => {
    setSelectedUser(user);
    setSelectedRole(user.role_id);

    // Use overridden_role to set selectedRolePermissions
    setSelectedRolePermissions({
      can_add_invoice: user.overridden_role.can_add_invoice,
      can_access_scans: user.overridden_role.can_access_scans,
      can_access_all_scans: user.overridden_role.can_access_all_scans,
      can_access_all_company_scans: user.overridden_role.can_access_all_company_scans,
      can_delete_invoice: user.overridden_role.can_delete_invoice,
      can_add_expense: user.overridden_role.can_add_expense,
      can_access_expense: user.overridden_role.can_access_expense,
      can_general_scan: user.overridden_role.can_general_scan,
      can_user_management: user.overridden_role.can_user_management,
      can_settings: user.overridden_role.can_settings,
      can_vendor: user.overridden_role.can_vendor,
      can_configurator: user.overridden_role.can_configurator,
      can_delete_documents: user.overridden_role.can_delete_documents,
      can_add_documents: user.overridden_role.can_add_documents,
      can_manage_documents: user.overridden_role.can_manage_documents,
      can_function_dashboard: user.overridden_role.can_function_dashboard,
      can_company_dashboard: user.overridden_role.can_company_dashboard,
      can_enterprise_dashboard: user.overridden_role.can_enterprise_dashboard,
    });

    setCustomPermissions({
      can_add_invoice: user.overridden_role.can_add_invoice,
      can_access_scans: user.overridden_role.can_access_scans,
      can_access_all_scans: user.overridden_role.can_access_all_scans,
      can_access_all_company_scans: user.overridden_role.can_access_all_company_scans,
      can_delete_invoice: user.overridden_role.can_delete_invoice,
      can_add_expense: user.overridden_role.can_add_expense,
      can_access_expense: user.overridden_role.can_access_expense,
      can_general_scan: user.overridden_role.can_general_scan,
      can_user_management: user.overridden_role.can_user_management,
      can_settings: user.overridden_role.can_settings,
      can_vendor: user.overridden_role.can_vendor,
      can_configurator: user.overridden_role.can_configurator,
      can_delete_documents: user.overridden_role.can_delete_documents,
      can_add_documents: user.overridden_role.can_add_documents,
      can_manage_documents: user.overridden_role.can_manage_documents,
      can_function_dashboard: user.overridden_role.can_function_dashboard,
      can_company_dashboard: user.overridden_role.can_company_dashboard,
      can_enterprise_dashboard: user.overridden_role.can_enterprise_dashboard,
    });

    setEditingPermissions(true);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingPermissions(false);
    setCustomPermissions({
      can_add_invoice: false,
      can_access_scans: false,
      can_access_all_scans: false,
      can_access_all_company_scans: false,
      can_delete_invoice: false,
      can_add_expense: false,
      can_access_expense: false,
      can_general_scan: false,
      can_user_management: false,
      can_settings: false,
      can_vendor: false,
      can_configurator: false,
      can_delete_documents: false,
      can_add_documents: false,
      can_manage_documents: false,
      can_function_dashboard: false,
      can_company_dashboard: false,
      can_enterprise_dashboard: false,
    });
  };

  const handleDelete = (event, user) => {
    setSelectedUserToDelete(user);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-user?id=${selectedUserToDelete.id}`);
      enqueueSnackbar(`User ${selectedUserToDelete.name} deleted successfully`, { variant: "success" });
      onUserUpdated(selectedRole);
    } catch (error) {
      console.error("Error deleting user:", error);
      enqueueSnackbar("An error occurred while deleting user", { variant: "error" });
    }
    setShowDeleteConfirmation(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const handleRoleChange = (event) => {
    const selectedRoleId = event.target.value;
    setSelectedRole(selectedRoleId);

    // Use selectedRoleId to set selectedRolePermissions and customPermissions
    const selectedRole = roles.find(role => role.id === selectedRoleId);
    if (selectedRole) {
      setSelectedRolePermissions({
        can_add_invoice: selectedRole.can_add_invoice,
        can_access_scans: selectedRole.can_access_scans,
        can_access_all_scans: selectedRole.can_access_all_scans,
        can_access_all_company_scans: selectedRole.can_access_all_company_scans,
        can_delete_invoice: selectedRole.can_delete_invoice,
        can_add_expense: selectedRole.can_add_expense,
        can_access_expense: selectedRole.can_access_expense,
        can_general_scan: selectedRole.can_general_scan,
        can_user_management: selectedRole.can_user_management,
        can_settings: selectedRole.can_settings,
        can_vendor: selectedRole.can_vendor,
        can_configurator: selectedRole.can_configurator,
        can_delete_documents: selectedRole.can_delete_documents,
        can_add_documents: selectedRole.can_add_documents,
        can_manage_documents: selectedRole.can_manage_documents,
        can_function_dashboard: selectedRole.can_function_dashboard,
        can_company_dashboard: selectedRole.can_company_dashboard,
        can_enterprise_dashboard: selectedRole.can_enterprise_dashboard,
      });

      setCustomPermissions(prevState => ({
        ...prevState,
        can_add_invoice: selectedRole.can_add_invoice,
        can_access_scans: selectedRole.can_access_scans,
        can_access_all_scans: selectedRole.can_access_all_scans,
        can_access_all_company_scans: selectedRole.can_access_all_company_scans,
        can_delete_invoice: selectedRole.can_delete_invoice,
        can_add_expense: selectedRole.can_add_expense,
        can_access_expense: selectedRole.can_access_expense,
        can_general_scan: selectedRole.can_general_scan,
        can_user_management: selectedRole.can_user_management,
        can_settings: selectedRole.can_settings,
        can_vendor: selectedRole.can_vendor,
        can_configurator: selectedRole.can_configurator,
        can_delete_documents: selectedRole.can_delete_documents,
        can_add_documents: selectedRole.can_add_documents,
        can_manage_documents: selectedRole.can_manage_documents,
        can_function_dashboard: selectedRole.can_function_dashboard,
        can_company_dashboard: selectedRole.can_company_dashboard,
        can_enterprise_dashboard: selectedRole.can_enterprise_dashboard,
      }));
    }
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setSelectedUser({ ...selectedUser, email });

    // Check if the email already exists in the users array
    const emailExists = users.some((user) => user.email === email);
    if (emailExists) {
      setEmailError('Email already exists');
    } else {
      setEmailError(null);
    }
  };

  const handleCodeChange = (event, newValue) => {
    const employee_code = newValue ? newValue.employee_code : '';
    setSelectedUser({ ...selectedUser, employee_code });

    // Check if the employee code already exists in the users array
    const codeExists = users.some((user) => user.employee_code === employee_code);
    if (codeExists) {
      setCodeError('Employee code already exists');
    } else {
      setCodeError(null);
    }

    // Fetch employee data and auto-fill name and email fields
    if (employee_code) {
      httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-employees?employee_code=${employee_code}`)
        .then(response => {
          console.log("Employee Data Response:", response.data);
          const employee = response.data.employees[0];
          if (employee) {
            setSelectedUser({ ...selectedUser, name: employee.employee_name, email: employee.email, employee_code });
          } else {
            console.warn("No employee found for code:", employee_code);
          }
        })
        .catch(error => {
          console.error("Error fetching employee data:", error);
          enqueueSnackbar("Error fetching employee data", { variant: "error" });
        });
    }
  };

  const handleDmsUserChange = (event, newValue) => {
    setSelectedUser({
      ...selectedUser,
      dms_user_id: newValue?.id || "",
    });
  };

  const handleSave = async () => {
    try {
      const requestBody = {
        user_id: selectedUser.id,
        role_id: selectedRole,
        role_overrides: editingPermissions,
        dms_user_id: selectedUser.dms_user_id || "",
      };

      if (editingPermissions) {
        requestBody.custom_permissions = customPermissions;
      }

      console.log("Save Request Body:", requestBody); // Debug log
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/edit-user`, requestBody);

      onUserUpdated(selectedUser.id, selectedRole);
      enqueueSnackbar("User updated successfully", { variant: "success" });
    } catch (error) {
      console.log("Error updating user:", error);
      enqueueSnackbar("An error occurred while updating user role", { variant: "error" });
    }

    setShowModal(false);
    setEditingPermissions(false);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleCreateUser = () => {
    setSelectedUser({ name: "", email: "", password: "", employee_code: "", dms_user_id: "" });
    setSelectedRole("");
    setShowCreateUserModal(true);
  };

  const handleCreateUserClose = () => {
    setShowCreateUserModal(false);
  };

  const handleCreateUserSave = async () => {
    const errors = {};

    // Check if name is empty
    if (!selectedUser?.name) {
      errors.name = 'Name is required';
    }

    // Check if email is empty
    if (!selectedUser?.email) {
      errors.email = 'Email is required';
    }

    // Check if pass is empty
    if (!selectedUser?.password) {
      errors.password = 'Password is required';
    }

    // Check if code is empty
    if (!selectedUser?.employee_code) {
      errors.employee_code = 'Employee Code is required';
    }

    // Check if role is empty
    if (!selectedRole) {
      errors.role = 'Role is required';
    }

    // Update formErrors state with errors
    setFormErrors(errors);

    // If there are no errors, proceed with creating the user
    if (Object.keys(errors).length === 0) {
      try {
        const requestBody = {
          name: selectedUser.name,
          email: selectedUser.email,
          password: selectedUser.password,
          employee_code: selectedUser.employee_code,
          role_id: selectedRole,
          role_overrides: true,
          dms_user_id: selectedUser.dms_user_id || "",
        };

        // Copy custom permissions of selected role to overridden role
        const selectedRoleData = roles.find(role => role.id === selectedRole);
        if (selectedRoleData) {
          requestBody.custom_permissions = {
            can_add_invoice: selectedRoleData.can_add_invoice,
            can_access_scans: selectedRoleData.can_access_scans,
            can_access_all_scans: selectedRoleData.can_access_all_scans,
            can_access_all_company_scans: selectedRoleData.can_access_all_company_scans,
            can_delete_invoice: selectedRoleData.can_delete_invoice,
            can_add_expense: selectedRoleData.can_add_expense,
            can_access_expense: selectedRoleData.can_access_expense,
            can_general_scan: selectedRoleData.can_general_scan,
            can_user_management: selectedRoleData.can_user_management,
            can_settings: selectedRoleData.can_settings,
            can_vendor: selectedRoleData.can_vendor,
            can_configurator: selectedRoleData.can_configurator,
            can_delete_documents: selectedRoleData.can_delete_documents,
            can_add_documents: selectedRoleData.can_add_documents,
            can_manage_documents: selectedRoleData.can_manage_documents,
            can_function_dashboard: selectedRoleData.can_function_dashboard,
            can_company_dashboard: selectedRoleData.can_company_dashboard,
            can_enterprise_dashboard: selectedRoleData.can_enterprise_dashboard,
          };
        }

        console.log("Create User Request Body:", requestBody); // Debug log
        const response = await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/create-user`, requestBody);
        onUserUpdated(response.data.id, selectedRole);
        enqueueSnackbar("User created successfully", { variant: "success" });
        setSelectedUser({ name: "", email: "", password: "", employee_code: "", dms_user_id: "" });
      } catch (error) {
        console.error("Error creating user:", error);
        enqueueSnackbar("An error occurred while creating user", { variant: "error" });
      }

      setShowCreateUserModal(false);
      setEditingPermissions(false);
    }
  };

  const handleCustomizePermissions = () => {
    setEditingPermissions(true);
  };

  const handleCustomPermissionChange = (key, value) => {
    setCustomPermissions(prevState => ({
      ...prevState,
      [key]: value
    }));
  };

  const handleResetPassword = (event, user) => {
    setSelectedUser(user);
    setSelectedUserId(user.id);
    setShowResetPasswordDialog(true);
  };

  const handleConfirmPassword = async () => {
    try {
      const requestBody = {
        user_id: selectedUserId,
        newPassword: newPassword,
      };

      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/change-user-password`, requestBody);
      enqueueSnackbar("Password reset successfully", { variant: "success" });
      setShowResetPasswordDialog(false);
      resetPasswordState();
    } catch (error) {
      console.error("Error resetting password:", error);
      enqueueSnackbar("An error occurred while resetting password", { variant: "error" });
    }
  };

  const handleCreateRoleDialogOpen = () => {
    setDialogTitle("Create Role");
    setFormData({
      name: "",
      can_add_invoice: false,
      can_access_scans: false,
      can_access_all_scans: false,
      can_access_all_company_scans: false,
      can_delete_invoice: false,
      can_add_expense: false,
      can_access_expense: false,
      can_general_scan: false,
      can_user_management: false,
      can_settings: false,
      can_vendor: false,
      can_configurator: false,
      can_delete_documents: false,
      can_add_documents: false,
      can_manage_documents: false,
      can_function_dashboard: false,
      can_company_dashboard: false,
      can_enterprise_dashboard: false
    });
    setOpenDialog(true);
  };

  const handleCreateRoleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleCreateRoleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: checked
    }));
    if (name === "can_access_scans" && !checked) {
      setFormData(prevData => ({
        ...prevData,
        can_access_all_scans: false,
        can_access_all_company_scans: false
      }));
    }
  };

  const handleCreateRoleSwitchChange = (name) => {
    setFormData(prevData => {
      const newFormData = { ...prevData };

      if (name === "can_access_all_scans") {
        newFormData.can_access_all_scans = !prevData.can_access_all_scans;
        if (newFormData.can_access_all_scans) {
          newFormData.can_access_all_company_scans = true;
        }
      } else if (name === "can_access_all_company_scans") {
        newFormData.can_access_all_company_scans = !prevData.can_access_all_company_scans;
        if (!newFormData.can_access_all_company_scans) {
          newFormData.can_access_all_scans = false;
        }
      }

      // If both are toggled and company scans is turned off, turn off all scans as well
      if (newFormData.can_access_all_scans && !newFormData.can_access_all_company_scans) {
        newFormData.can_access_all_scans = false;
      }

      return newFormData;
    });
  };

  const handleCreateRoleNameChange = (e) => {
    const { value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      name: value
    }));
  };

  const handleCreateRoleSubmit = async () => {
    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/create-role`, formData);
      fetchRoles();
      setOpenDialog(false);
      enqueueSnackbar("Role created successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to create role", { variant: "error" });
    }
  };

  // Transform users data to include company_name and function_name
  const transformedUsers = users.map(user => {
    let userRole;
    userRole = user.overridden_role;

    return {
      id: user.user_id,
      ...user,
      role_name: roles.find(role => role.id === user.role_id)?.name || "Unknown Role", // Use original role name if user has custom permissions
      isDmsUser: !!user.dms_user_id, // Add isDmsUser field based on dms_user_id presence
    };
  });

  // Filter out users with role_id 1
  const filteredOutRoleId1Users = transformedUsers.filter(user => user.role_id !== 1);

  // Further filter based on the search string
  const filteredUsers = filteredOutRoleId1Users.filter(user => {
    const searchString = search.toLowerCase();

    return (
      user.name.toLowerCase().includes(searchString) ||
      user.email.toLowerCase().includes(searchString) ||
      user.employee_code.toLowerCase().includes(searchString) ||
      user.role_name.toLowerCase().includes(searchString)
    );
  });

  // Filter out employees already associated with users
  const availableEmployees = employees.filter(employee => 
    !users.some(user => user.employee_code === employee.employee_code)
  );

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginTop: "20px", marginBottom: "20px" }}>
        <Card style={{ width: "90%", overflow: "hidden" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2 }}>
            <StyledTextField
              label="Search..."
              variant="outlined"
              size="small"
              value={search}
              onChange={handleSearchChange}
            />

            {/* Spacer to push buttons to the right */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Buttons aligned to the right */}
            <ButtonOutlined onClick={handleCreateUser} style={{ marginLeft: 4 }}>
              <PersonAddIcon /> New User
            </ButtonOutlined>
            <ButtonOutlined onClick={handleCreateRoleDialogOpen} style={{ marginLeft: 4 }}>
              <BadgeIcon /> New Role
            </ButtonOutlined>
          </Box>
          <DataGrid
            sx={{ fontFamily: "SapFiori72, sans-serif" }}
            rows={filteredUsers}
            columns={[
              {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                renderCell: (params) => (
                  <Tooltip title={params.value}>
                    <span>{params.value}</span>
                  </Tooltip>
                ),
              },
              {
                field: 'employee_code',
                headerName: 'Employee Code',
                flex: 1,
                renderCell: (params) => (
                  <Tooltip title={params.value}>
                    <span>{params.value}</span>
                  </Tooltip>
                ),
              },
              {
                field: 'email',
                headerName: 'Email',
                flex: 1,
                renderCell: (params) => (
                  <Tooltip title={params.value}>
                    <span>{params.value}</span>
                  </Tooltip>
                ),
              },
              {
                field: 'role_name',
                headerName: 'Role',
                flex: 1,
                renderCell: (params) => (
                  <Tooltip title={params.value}>
                    <span>{params.value}</span>
                  </Tooltip>
                ),
              },
              {
                field: 'isDmsUser',
                headerName: 'DMS Assignment',
                flex: 1,
                renderCell: (params) => {
                  const displayValue = params.value.toString(); // Convert boolean to string ("true" or "false")
                  const capitalizedValue = displayValue.charAt(0).toUpperCase() + displayValue.slice(1); // Capitalize first letter
                  return (
                    <Tooltip title={capitalizedValue}>
                      <span>{capitalizedValue}</span>
                    </Tooltip>
                  );
                },
              },
              {
                field: "actions",
                headerName: "Actions",
                flex: 1,
                renderCell: (params) => (
                  <Box>
                    {params.row.role_id === 1 ? null : (
                      <>
                        <ButtonContained
                          onClick={(event) => handleEdit(event, params.row)}
                          style={{ marginRight: 8 }}
                        >
                          <Tooltip title="Edit User">
                            <EditIcon />
                          </Tooltip>
                        </ButtonContained>

                        <ButtonOutlined
                          onClick={(event) => handleDelete(event, params.row)}
                        >
                          <Tooltip title="Delete User">
                            <DeleteIcon />
                          </Tooltip>
                        </ButtonOutlined>

                        <ButtonOutlined
                          onClick={(event) => handleResetPassword(event, params.row)}
                          style={{ marginLeft: 8 }}
                        >
                          <Tooltip title="Reset Password">
                            <LockResetIcon />
                          </Tooltip>
                        </ButtonOutlined>
                      </>
                    )}
                  </Box>
                ),
              },
            ]}
            autoHeight
          />
        </Card>
      </div>

      <Dialog open={showModal} onClose={handleModalClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>Authorization Management for {selectedUser?.name}</DialogTitle>
        <DialogContent>
          {/* Add DMS User Dropdown for Edit User */}
          <Autocomplete
            options={dmsUsers}
            getOptionLabel={(option) => `${option.email} (${option.firstName} ${option.lastName})`}
            filterOptions={(options, state) => {
              return options.filter((option) => {
                const email = option?.email || '';
                const firstName = option?.firstName || '';
                const lastName = option?.lastName || '';
                return (
                  email.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                  firstName.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                  lastName.toLowerCase().includes(state.inputValue.toLowerCase())
                );
              });
            }}
            value={dmsUsers.find(dms => dms.id === selectedUser.dms_user_id) || null}
            onChange={handleDmsUserChange}
            renderInput={(params) => (
              <TextField
              margin="normal"
                {...params}
                label="Select DMS User"
              />
            )}
          />

          <FormControl fullWidth sx={{ marginTop: 2 }} className={classes.formControl}>
            <InputLabel sx={{ fontFamily: "SapFiori72, sans-serif" }}>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              label="Role"
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedRole && !editingPermissions && (
            <>
              <Typography sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", marginTop: 2 }} variant="subtitle1">Permissions</Typography>
              <Typography sx={{ fontFamily: "SapFiori72, sans-serif" }}>
                {[
                  { key: 'can_function_dashboard', label: 'Department-Level Insights' },
                  { key: 'can_company_dashboard', label: 'Company-Wide Insights' },
                  { key: 'can_enterprise_dashboard', label: 'Enterprise-Level Insights' },
                  { key: 'can_add_invoice', label: 'Can Add Invoice' },
                  { key: 'can_access_scans', label: 'Can Access Scans' },
                  { key: 'can_access_all_scans', label: 'Can Access All Scans' },
                  { key: 'can_access_all_company_scans', label: 'Can Access All Scans Within Their Company' },
                  { key: 'can_delete_invoice', label: 'Can Delete Invoices' },
                  { key: 'can_add_expense', label: 'Can Add Expense' },
                  { key: 'can_access_expense', label: 'Can Access Expenses' },
                  { key: 'can_add_documents', label: 'Can Add Documents' },
                  { key: 'can_manage_documents', label: 'Can Manage Documents' },
                  { key: 'can_delete_documents', label: 'Can Delete Documents' },
                  { key: 'can_general_scan', label: 'Can General Scan' },
                  { key: 'can_settings', label: 'Can Modify System Settings' },
                  { key: 'can_configurator', label: 'Can Configure Environment' },
                  { key: 'can_user_management', label: 'Can Manage Users' },
                  { key: 'can_vendor', label: 'Can Manage Vendors' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    {label}: {selectedRolePermissions[key] ? "Yes" : "No"}
                  </div>
                ))}
              </Typography>

              <ButtonContained style={{ marginTop: 8 }} onClick={handleCustomizePermissions}>Customize</ButtonContained>
            </>
          )}

          {editingPermissions && (
            <>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", marginTop: 2 }} component="legend">Permissions</FormLabel>
                <FormGroup>
                  {[
                    { key: 'can_function_dashboard', label: 'Department-Level Insights' },
                    { key: 'can_company_dashboard', label: 'Company-Wide Insights' },
                    { key: 'can_enterprise_dashboard', label: 'Enterprise-Level Insights' },
                    { key: 'can_add_invoice', label: 'Can Add Invoice' },
                    { key: 'can_access_scans', label: 'Can Access Scans' },
                    { key: 'can_access_all_scans', label: 'Can Access All Scans' },
                    { key: 'can_access_all_company_scans', label: 'Can Access All Scans Within Their Company' },
                    { key: 'can_delete_invoice', label: 'Can Delete Invoices' },
                    { key: 'can_add_expense', label: 'Can Add Expense' },
                    { key: 'can_access_expense', label: 'Can Access Expenses' },
                    { key: 'can_add_documents', label: 'Can Add Documents' },
                    { key: 'can_manage_documents', label: 'Can Manage Documents' },
                    { key: 'can_delete_documents', label: 'Can Delete Documents' },
                    { key: 'can_general_scan', label: 'Can General Scan' },
                    { key: 'can_settings', label: 'Can Modify System Settings' },
                    { key: 'can_configurator', label: 'Can Configure Environment' },
                    { key: 'can_user_management', label: 'Can Manage Users' },
                    { key: 'can_vendor', label: 'Can Manage Vendors' },
                  ].map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={customPermissions[key]}
                          onChange={(event) => handleCustomPermissionChange(key, event.target.checked)}
                          name={key}
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <ButtonOutlined style={{ marginRight: 5 }} onClick={handleModalClose}>Cancel</ButtonOutlined>
          <ButtonContained onClick={handleSave}>Save</ButtonContained>
        </DialogActions>
      </Dialog>

      <Dialog open={showCreateUserModal} onClose={handleCreateUserClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>Create New User</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={availableEmployees}
            getOptionLabel={(option) => `${option.employee_code} (${option.employee_name})`}
            filterOptions={(options, state) => {
              const filtered = options.filter((option) => {
                const employeeCode = option?.employee_code || '';
                const employeeName = option?.employee_name || '';
                return (
                  employeeCode.includes(state.inputValue) ||
                  employeeName.toLowerCase().includes(state.inputValue.toLowerCase())
                );
              });
              return filtered;
            }}
            value={availableEmployees.find(emp => emp.employee_code === selectedUser.employee_code) || null}
            onChange={handleCodeChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee Code"
                error={!!codeError || !!formErrors.employee_code}
                helperText={codeError || formErrors.employee_code}
              />
            )}
          />

          <StyledTextField
            margin="normal"
            fullWidth
            label="Name"
            value={selectedUser.name}
            onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
            error={!!formErrors.name}
            helperText={formErrors.name}
          />

          <StyledTextField
            margin="normal"
            fullWidth
            label="Email"
            value={selectedUser.email}
            onChange={handleEmailChange}
            error={!!emailError || !!formErrors.email}
            helperText={emailError || formErrors.email}
          />

          <StyledTextField
            margin="normal"
            fullWidth
            label="Password"
            type="password"
            value={selectedUser.password}
            onChange={(e) => setSelectedUser({...selectedUser, password: e.target.value})}
            error={!!formErrors.password}
            helperText={formErrors.password}
          />

          <Autocomplete
            options={dmsUsers}
            getOptionLabel={(option) => `${option.email} (${option.firstName} ${option.lastName})`}
            filterOptions={(options, state) => {
              return options.filter((option) => {
                const email = option?.email || '';
                const firstName = option?.firstName || '';
                const lastName = option?.lastName || '';
                return (
                  email.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                  firstName.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                  lastName.toLowerCase().includes(state.inputValue.toLowerCase())
                );
              });
            }}
            value={dmsUsers.find(dms => dms.id === selectedUser.dms_user_id) || null}
            onChange={handleDmsUserChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select DMS User"
              />
            )}
          />

          <FormControl fullWidth sx={{ mt: 2, mb: 2 }} className={classes.formControl} error={!!formErrors.role}>
            <InputLabel sx={{ fontFamily: "SapFiori72, sans-serif" }}>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              label="Role" sx={{ fontFamily: "SapFiori72, sans-serif" }}
            >
              {roles.map((role) => (
                <MenuItem sx={{ fontFamily: "SapFiori72, sans-serif" }} key={role.id} value={role.id}>{role.name}</MenuItem>
              ))}
            </Select>
            {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
          </FormControl>

          {selectedRole && !editingPermissions && (
            <>
              <Typography variant="subtitle1" sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", marginTop: 2 }} >Permissions</Typography>
              <Typography sx={{ fontFamily: "SapFiori72, sans-serif" }}>
                {[
                  { key: 'can_function_dashboard', label: 'Department-Level Insights' },
                  { key: 'can_company_dashboard', label: 'Company-Wide Insights' },
                  { key: 'can_enterprise_dashboard', label: 'Enterprise-Level Insights' },
                  { key: 'can_add_invoice', label: 'Can Add Invoice' },
                  { key: 'can_access_scans', label: 'Can Access Scans' },
                  { key: 'can_access_all_scans', label: 'Can Access All Scans' },
                  { key: 'can_access_all_company_scans', label: 'Can Access All Scans Within Their Company' },
                  { key: 'can_delete_invoice', label: 'Can Delete Invoices' },
                  { key: 'can_add_expense', label: 'Can Add Expense' },
                  { key: 'can_access_expense', label: 'Can Access Expenses' },
                  { key: 'can_add_documents', label: 'Can Add Documents' },
                  { key: 'can_manage_documents', label: 'Can Manage Documents' },
                  { key: 'can_delete_documents', label: 'Can Delete Documents' },
                  { key: 'can_general_scan', label: 'Can General Scan' },
                  { key: 'can_settings', label: 'Can Modify System Settings' },
                  { key: 'can_configurator', label: 'Can Configure Environment' },
                  { key: 'can_user_management', label: 'Can Manage Users' },
                  { key: 'can_vendor', label: 'Can Manage Vendors' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    {label}: {selectedRolePermissions[key] ? "Yes" : "No"}
                  </div>
                ))}
              </Typography>

              <ButtonContained style={{ marginTop: 8 }} onClick={handleCustomizePermissions}>Customize</ButtonContained>
            </>
          )}

          {editingPermissions && (
            <>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel component="legend" sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", marginTop: 2 }} >Permissions</FormLabel>
                <FormGroup>
                  {[
                    { key: 'can_function_dashboard', label: 'Department-Level Insights' },
                    { key: 'can_company_dashboard', label: 'Company-Wide Insights' },
                    { key: 'can_enterprise_dashboard', label: 'Enterprise-Level Insights' },
                    { key: 'can_add_invoice', label: 'Can Add Invoice' },
                    { key: 'can_access_scans', label: 'Can Access Scans' },
                    { key: 'can_access_all_scans', label: 'Can Access All Scans' },
                    { key: 'can_access_all_company_scans', label: 'Can Access All Scans Within Their Company' },
                    { key: 'can_delete_invoice', label: 'Can Delete Invoices' },
                    { key: 'can_add_expense', label: 'Can Add Expense' },
                    { key: 'can_access_expense', label: 'Can Access Expenses' },
                    { key: 'can_add_documents', label: 'Can Add Documents' },
                    { key: 'can_manage_documents', label: 'Can Manage Documents' },
                    { key: 'can_delete_documents', label: 'Can Delete Documents' },
                    { key: 'can_general_scan', label: 'Can General Scan' },
                    { key: 'can_settings', label: 'Can Modify System Settings' },
                    { key: 'can_configurator', label: 'Can Configure Environment' },
                    { key: 'can_user_management', label: 'Can Manage Users' },
                    { key: 'can_vendor', label: 'Can Manage Vendors' },
                  ].map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={customPermissions[key]}
                          onChange={(event) => handleCustomPermissionChange(key, event.target.checked)}
                          name={key}
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <ButtonOutlined style={{ marginRight: 5 }} onClick={handleCreateUserClose}>Cancel</ButtonOutlined>
          <ButtonContained onClick={handleCreateUserSave} {...(emailError ? { disabled: true } : {})}>
            Create
          </ButtonContained>
        </DialogActions>
      </Dialog>

      <Dialog open={showResetPasswordDialog} onClose={() => {
        setShowResetPasswordDialog(false);
        resetPasswordState();
      }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>Reset Password for {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <StyledTextField
            margin="normal"
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <StyledTextField
            margin="normal"
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <ButtonOutlined
            style={{ marginRight: 5 }}
            onClick={() => setShowResetPasswordDialog(false)}
          >
            Cancel
          </ButtonOutlined>
          <ButtonContained
            onClick={handleConfirmPassword}
            {...(newPassword === "" || confirmPassword === "" || newPassword !== confirmPassword ? { disabled: true } : {})}
          >
            Confirm
          </ButtonContained>
        </DialogActions>
      </Dialog>

      <Dialog open={showDeleteConfirmation} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>Confirm Delete User</DialogTitle>
        <DialogContent>
          Are you sure you want to delete user {selectedUserToDelete?.name}?
        </DialogContent>
        <DialogActions>
          <ButtonOutlined style={{ marginRight: 5 }} onClick={handleDeleteCancel}>Cancel</ButtonOutlined>
          <ButtonContained onClick={handleDeleteConfirm} design="Emphasized">
            Delete
          </ButtonContained>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={handleCreateRoleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>{dialogTitle}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Label>Name</Label>
            <Input placeholder="Name" value={formData.name} onInput={handleCreateRoleNameChange} />
            <Label>KPI Access Permissions</Label>
            <CheckBox text="Department-Level Insights" name="can_function_dashboard" checked={formData.can_function_dashboard} onChange={handleCreateRoleCheckboxChange} />
            <CheckBox text="Company-Wide Insights" name="can_company_dashboard" checked={formData.can_company_dashboard} onChange={handleCreateRoleCheckboxChange} />
            <CheckBox text="Enterprise-Level Insights" name="can_enterprise_dashboard" checked={formData.can_enterprise_dashboard} onChange={handleCreateRoleCheckboxChange} />
            <Label>Invoice Permissions</Label>
            <CheckBox text="Can Add Invoice" name="can_add_invoice" checked={formData.can_add_invoice} onChange={handleCreateRoleCheckboxChange} />
            <CheckBox text="Can Access Scans" name="can_access_scans" checked={formData.can_access_scans} onChange={handleCreateRoleCheckboxChange} />
            <div style={{ marginBottom: -10, marginTop: -10, marginLeft: '1rem', marginRight: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Label style={{ marginRight: '10px' }}>All Scans</Label>
                <Switch
                  checked={formData.can_access_all_scans}
                  disabled={!formData.can_access_scans}
                  onChange={() => handleCreateRoleSwitchChange("can_access_all_scans")}
                />
              </div>
            </div>
            <div style={{ marginLeft: '1rem', marginRight: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Label style={{ marginRight: '10px' }}>All Functions Within Company(s)</Label>
                <Switch
                  checked={formData.can_access_all_company_scans}
                  disabled={!formData.can_access_scans}
                  onChange={() => handleCreateRoleSwitchChange("can_access_all_company_scans")}
                />
              </div>
            </div>
            <CheckBox text="Can Delete Invoices" name="can_delete_invoice" checked={formData.can_delete_invoice} onChange={handleCreateRoleCheckboxChange} />
            <Label>Expense Permissions</Label>
            <CheckBox text="Can Add Expense" name="can_add_expense" checked={formData.can_add_expense} onChange={handleCreateRoleCheckboxChange} />
            <Label>Document Management</Label>
            <CheckBox text="Can Upload Documents" name="can_add_documents" checked={formData.can_add_documents} onChange={handleCreateRoleCheckboxChange} />
            <CheckBox text="Can Manage Documents" name="can_manage_documents" checked={formData.can_manage_documents} onChange={handleCreateRoleCheckboxChange} />
            <CheckBox text="Can Delete Documents" name="can_delete_documents" checked={formData.can_delete_documents} onChange={handleCreateRoleCheckboxChange} />
            <Label>General Permissions</Label>
            <CheckBox text="Can General Scan" name="can_general_scan" checked={formData.can_general_scan} onChange={handleCreateRoleCheckboxChange} />
            <Label>System Permissions</Label>
            <CheckBox text="Can Modify Settings" name="can_settings" checked={formData.can_settings} onChange={handleCreateRoleCheckboxChange} />
            <CheckBox text="Can Configure Environment" name="can_configurator" checked={formData.can_configurator} onChange={handleCreateRoleCheckboxChange} />
            <CheckBox text="Can Manage Users" name="can_user_management" checked={formData.can_user_management} onChange={handleCreateRoleCheckboxChange} />
            <CheckBox text="Can Manage Vendors" name="can_vendor" checked={formData.can_vendor} onChange={handleCreateRoleCheckboxChange} />
          </div>
        </DialogContent>
        <DialogActions>
          <ButtonOutlined style={{ marginRight: 5 }} onClick={handleCreateRoleDialogClose}>Cancel</ButtonOutlined>
          <ButtonContained onClick={handleCreateRoleSubmit}>Create</ButtonContained>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UsersTable;